/**
 * App API client — Cognito-authenticated operations.
 *
 * Covers everything the public API contract doesn't expose: profile,
 * BYOK provider-key storage, Stripe billing, and the conversation-history
 * sidebar. (File attachments go through the public API as inline chat
 * content blocks — see src/attachments.ts.) Auth is the Cognito JWT pulled
 * from the Amplify Auth session on every call.
 *
 * On sign-in, the app calls `POST /app/profile/api-key` exactly once.
 * That endpoint is idempotent: if the user already has an active key it
 * returns metadata only; if not, it creates a new key and returns the
 * raw value, which the caller stashes in sessionStorage for use by
 * `aridel-client.ts`.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import { API_KEY_STORAGE_KEY, APP_API_BASE_URL } from './config';

// Normalize once so a base with/without a trailing slash both work; every
// path below starts with `/app/...`.
const BASE = APP_API_BASE_URL.replace(/\/$/, '');

export class AppApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function jwt(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new AppApiError('No Cognito session. Sign in first.', 401);
  }
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${BASE}${path}`;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${await jwt()}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(url, { ...init, headers });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new AppApiError(
      body?.error ?? `${resp.status} ${resp.statusText}`,
      resp.status,
    );
  }
  return resp.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Sign-in handshake — call once after Cognito sign-in succeeds.
// ---------------------------------------------------------------------------

export interface ApiKeyHandshakeResponse {
  api_key: string | null;
  has_key: boolean;
  key_prefix: string;
  created_at?: string;
  name?: string;
}

export async function fetchOrCreateApiKey(): Promise<ApiKeyHandshakeResponse> {
  const resp = await request<ApiKeyHandshakeResponse>('/app/profile/api-key', {
    method: 'POST',
  });
  if (resp.api_key) {
    sessionStorage.setItem(API_KEY_STORAGE_KEY, resp.api_key);
  }
  return resp;
}

export function clearApiKey() {
  sessionStorage.removeItem(API_KEY_STORAGE_KEY);
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export const profile = {
  get: () => request<Record<string, unknown>>('/app/profile'),
  updatePreferences: (prefs: Record<string, unknown>) =>
    request<Record<string, unknown>>('/app/profile/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
};

// ---------------------------------------------------------------------------
// BYOK
// ---------------------------------------------------------------------------

// Bedrock batch (cross-account role-assumption) config — stored as plain,
// non-secret Profiles attributes (distinct from the chat bearer key).
export interface BedrockBatchConfig {
  role_arn: string;
  s3_bucket: string;
  region: string;
  s3_prefix?: string;
  service_role_arn?: string;
  external_id?: string;
}

export interface ByokSaveOptions {
  region?: string;                  // Bedrock chat key region
  batchConfig?: BedrockBatchConfig; // Bedrock batch role config
}

export const byok = {
  // Backend (settings.py handle_save_key) reads `apiKey` (camelCase). Bedrock
  // also accepts `region` (chat key) and a `batchConfig` block (batch role).
  save: (provider: string, apiKey: string, opts: ByokSaveOptions = {}) =>
    request('/app/byok', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        apiKey,
        ...(opts.region ? { region: opts.region } : {}),
        ...(opts.batchConfig ? { batchConfig: opts.batchConfig } : {}),
      }),
    }),
  remove: (provider: string) =>
    request(`/app/byok/${encodeURIComponent(provider)}`, { method: 'DELETE' }),
  test: (provider: string) =>
    request<{ valid: boolean; error?: string }>('/app/byok/test', {
      method: 'POST',
      body: JSON.stringify({ provider }),
    }),
};

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

// Field names mirror settings.py exactly: request `amount_dollars`,
// response `checkout_url`.
export type PlanTier = 'free' | 'team' | 'scale' | 'enterprise';

export const billing = {
  checkout: (amountDollars: number) =>
    request<{ checkout_url: string }>('/app/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ amount_dollars: amountDollars }),
    }),
  // Org-ladder plan purchase (Stripe subscription checkout). `plan` is
  // 'team' | 'scale'; Enterprise is sold manually.
  subscribe: (plan: Exclude<PlanTier, 'free' | 'enterprise'>) =>
    request<{ checkout_url: string }>('/app/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),
  // Stripe customer portal: change / cancel the plan, invoices.
  portal: () =>
    request<{ portal_url: string }>('/app/billing/portal', { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Conversation history
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  conversation_id: string;
  title: string;
  last_updated_at: string;
  created_at?: string;
  message_count?: number;
  file_count?: number;
  first_assistant_preview?: string;
}

export interface ConversationMessage {
  conversation_id: string;
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  model_used?: string;
  files?: Array<Record<string, unknown>>;
  token_usage?: Record<string, unknown>;
  routing_details?: Record<string, unknown>;
  web_search_enabled?: boolean;
}

/** Payload for persisting a message pair after a public-API completion. */
export interface AppendMessagesPayload {
  user_message: { content: string; files?: Array<Record<string, unknown>> };
  assistant_message: {
    content: string;
    model_used?: string;
    routing_details?: Record<string, unknown>;
    token_usage?: Record<string, unknown>;
    web_search_enabled?: boolean;
  };
}

export const conversations = {
  list: () =>
    request<ConversationSummary[]>('/app/conversations'),
  get: (conversationId: string) =>
    request<ConversationMessage[]>(
      `/app/conversations/${encodeURIComponent(conversationId)}`,
    ),
  /** Persist a user+assistant pair (the public API is stateless). */
  appendMessages: (conversationId: string, payload: AppendMessagesPayload) =>
    request<{ conversation_id: string; title: string }>(
      `/app/conversations/${encodeURIComponent(conversationId)}/messages`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  rename: (conversationId: string, title: string) =>
    request(`/app/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    }),
  delete: (conversationId: string) =>
    request(`/app/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    }),
};

export const appApi = {
  fetchOrCreateApiKey,
  clearApiKey,
  profile,
  byok,
  billing,
  conversations,
};
